// The Cattle Ticker — the left column of design/Main.dc.html.
//
// Owns the scope, the refresh loop and the "what changed" bookkeeping. Renders
// the artboard's four stacked pieces: the navy head (title, view toggle, live
// dot, meta line, filters), the DVAuction statement band, the crawling tape,
// and the 4×2 board of weight bands ending in the cull tile.
//
// ── Why this polls instead of subscribing ─────────────────────────────────
// The platform's other tickers use Supabase Realtime, and that is right for
// them: a logged-in board with a handful of viewers. This is the public front
// door. A websocket per visitor is a connection per visitor, and the one thing
// this page must survive is everybody arriving at once.
//
// Polling through /api/market costs the database nothing by comparison: the CDN
// answers from cache, so a hundred thousand visitors on a 30-second tick are
// still two queries a minute. The interval matches that TTL deliberately —
// staleness is the SUM of the two, so asking faster than the edge revalidates
// just re-reads the same bytes.
//
// ── Why the first render never fetches ────────────────────────────────────
// The tape is prerendered into the served HTML and hydrated here. Fetching
// during render would produce a different tree than the one in the document and
// fail hydration for the WHOLE page. So the first paint is always `initial`,
// and every refresh happens in an effect, after mount.

import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import {
  centralTime,
  EMPTY_TICKER,
  fetchBandCompare,
  fetchCattleTicker,
  fetchTickerBarns,
  marketStatus,
  vsWeekdayLabel,
  todayCentral,
  viewMixesClasses,
  type BandCompare,
  type CattleView,
  type MarketStatus,
  type TickerBarn,
  type TickerPayload,
  type TickerSummary,
  vsTypicalPhrase,
} from "@/lib/rbCattleTicker";

import { barnSlug } from "@/lib/barnSlug";
import { STATE_NAME_TO_ALPHA } from "@/lib/regions";


import BandBoard from "./BandBoard";
import LiveBarnRail from "./LiveBarnRail";
import { FLASH_MS } from "./useFreshLots";
import { StatementBand } from "./TickerParts";
import { ClassTabs, TickerHead } from "./TickerShell";

// Lazy, and the reason is size rather than habit. The map carries ~101 KB of
// precomputed state outlines and barn coordinates — cheap next to the ~1 MB of
// TopoJSON the interior map fetches per view, but not free, and the BOARD is
// the default. Loading it eagerly would make everyone pay for a panel most
// visitors never open.
const TickerMap = lazy(() => import("./TickerMap"));

/** "MO" → "Missouri". */
const STATE_NAME = Object.fromEntries(
  Object.entries(STATE_NAME_TO_ALPHA).map(([name, alpha]) => [alpha, name]),
) as Record<string, string>;

/** Matches the s-maxage on /api/market (30s). See the header. */
const POLL_MS = 30_000;

export default function TickerBoard({
  initial,
  compact = false,
  lockBarn = null,
  lockState = null,
  myBarns = null,
  liveRail = false,
  initialBarns,
  below,
  liveView,
  defaultView = null,
}: {
  initial: TickerPayload;
  /**
   * Something to render directly under the price bands, in the board's own
   * column — beside the barn rail, left of the page's right sidebar. Owner,
   * 2026-10-07, pointing at the blank space the bands leave above the rail's
   * tail: "i want it in this empty spot under the ticker between the left and
   * right sidebar." The homepage passes the Daily Live Price board. When set,
   * the bands and it share one column so the grid's two columns stay rail +
   * board; when unset nothing about the markup changes.
   */
  below?: ReactNode;
  /** What the Live Prices view shows in the ticker's place (owner, 2026-10-08:
   *  the weekly Daily Live Price grid). Falls back to `below`. */
  liveView?: ReactNode;
  /** Which view to open on once the page knows who is looking (owner, 2026-11-03: signed out the
   *  Cattle Ticker, signed in Live Prices). Null while that is still being read: the board stays on
   *  Ticker, which is what the served HTML shows. Applied once, and never over a tab the visitor
   *  has already picked. */
  defaultView?: "live" | "board" | "map" | null;

  /**
   * Show the live barn rail beside the board, in the width freed by taking the
   * grid from four columns to three (owner, 2026-09-30).
   *
   * ⚠️ OPT-IN, LIKE homeCard. This component also renders the phone app's
   * Prices and Barn Board screens, where a second column does not fit and the
   * rail's four per-barn links go to website routes. Only the website homepage
   * (RbTickerHome) passes it.
   */
  liveRail?: boolean;
  /** Barns + receipts baked by the prerender, so the rail has rows in the
   *  served HTML rather than "No sales reported yet". Seeds state only; the
   *  30s poll owns it from mount. */
  initialBarns?: TickerBarn[];
  /**
   * The phone app's head (owner, 2026-09-16, looking at the app on his phone).
   *
   * Same head, same controls, folded smaller. The website's head runs the title,
   * the date plate, Board|Map, a Scope picker, a Barn picker and the class tabs
   * down the screen, which on a 390px phone is the whole first screen before a
   * single price. In the app the class tabs stay out (they are the control that
   * gets used), and Scope, Barn and Board|Map move behind a Filter button.
   *
   * ⚠️ A FLAG ON THE REAL COMPONENT, not a second board. The app renders the
   * website's board precisely so the two cannot drift; forking the head to save
   * six rows would start the drift in the one place a reader looks first.
   */
  compact?: boolean;
  /**
   * Lock the board to ONE barn: no scope pickers, the barn's name as the
   * title, and every read scoped by its slug.
   *
   * ⚠️ A SLUG, NEVER A NAME. ok-stateline-stockyards trades as both "Stateline
   * Stockyards" and "StateLine Stockyards"; matching one spelling showed 177 of
   * its 946 lots. The slug resolves every name a barn has traded under — see
   * TickerScope.barnSlug.
   *
   * It is a prop on this component rather than a second board so the app's barn
   * screen gets the one fetch loop, the one refresh tick and the one head that
   * everything else already uses.
   */
  lockBarn?: { slug: string; name: string; state: string } | null;
  /**
   * Lock the board to ONE state (the RaisedBeef app's state page, owner
   * 2026-10-09): the state is the scope and the title, and the pickers that
   * could only navigate away from it are hidden. Every read is the same
   * { state } scope the homepage's state picker sends. Absent on the website.
   */
  lockState?: { alpha: string; name: string } | null;
  /**
   * The barn slugs this person starred, for the RaisedBeef app's My Barns
   * toggle (owner, 2026-09-20: "add a toggle on Home Screen").
   *
   * ⚠️ THE APP PASSES THIS. THE WEBSITE DOES NOT, AND MUST NOT. Owner, same
   * day: "This is only for the app". Left undefined, not one line below
   * behaves differently — the toggle is not rendered, no slug list is sent,
   * and the RPCs take their default of null. That is what makes it safe for
   * this shared component to carry an app-only feature at all.
   *
   * Empty or absent means no toggle: a My Barns switch that produces a blank
   * board is worse than no switch.
   */
  myBarns?: string[] | null;
}) {
  const [data, setData] = useState<TickerPayload>(initial ?? EMPTY_TICKER);
  const [barns, setBarns] = useState<TickerBarn[]>(initialBarns ?? []);
  const [state, setState] = useState<string | null>(lockState?.alpha ?? null);
  const [barn, setBarn] = useState<string | null>(null);
  /** My Barns on? App only, and only ever true when myBarns has slugs in it. */
  const [mine, setMine] = useState(false);
  /**
   * ⚠️ DERIVED, NOT TRUSTED. `mine` can outlive the list: unstar the last barn
   * with the toggle on and the switch disappears while the state stays true,
   * which would leave a man on a board he has no way to turn off.
   */
  const showMine = mine && (myBarns?.length ?? 0) > 0;
  const [view, setView] = useState<CattleView>("Steers");
  // Three views (owner, 2026-10-08): "Live Prices" — the Daily Live Price
  // board, which replaces the ticker boards; "Ticker" — the weight-band
  // boards; "Map". Live Prices exists only where `below` is given (the
  // website homepage); the barn and state boards have Ticker and Map.
  // The Quotes Board and Weekly Averages had toggles here for an afternoon
  // (2026-10-08) and moved to their own pages under Tools, as every-barn
  // boards; the homepage is the eleven-state indexes and the live pieces.
  const [boardView, setBoardView] = useState<"live" | "board" | "map">("board");
  const pickedView = useRef(false);
  useEffect(() => {
    if (!defaultView || pickedView.current) return;
    if (defaultView === "live" && !(liveView || below)) return;
    setBoardView(defaultView);
  }, [defaultView, liveView, below]);
  const showMap = boardView === "map";
  /** Last week's band averages, for the move and the chart's second line.
   *  App only — see fetchBandCompare for why it is not folded into the board's
   *  own fetch. Empty until it lands, and the board simply shows no move. */
  const [compare, setCompare] = useState<BandCompare>(() => new Map());
  /** Null until mounted, and that is the point: whether the ring is running
   *  depends on the CURRENT TIME, which the build machine does not share with
   *  the visitor. Baking an answer into a document served for days would have
   *  the page claiming to be live all weekend. */
  const [status, setStatus] = useState<MarketStatus | null>(null);
  /** Lot ids that arrived on the most recent refresh — used only to flash the
   *  new rows, so the board reads as live rather than as a static table. */
  const [fresh, setFresh] = useState<Set<string>>(() => new Set());
  /**
   * The clock time of the last read that actually got through, or null if none
   * has. Drives the "can't reach" line.
   *
   * ⚠️ A FAILED READ NO LONGER WIPES THE BOARD. It used to replace the prices
   * with an empty payload, which rendered as eight bands reading "No sales" —
   * telling a man the market was empty when his phone simply had no signal.
   * Yesterday's prices with the time on them are worth something; a false empty
   * board is worth less than nothing.
   */
  const [seenAt, setSeenAt] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);

  // Guards against an out-of-order response overwriting a newer one: switching
  // scope twice quickly can land the FIRST reply last, which would show
  // Nebraska's board under Kansas's heading.
  const reqRef = useRef(0);
  const seenRef = useRef<Set<string> | null>(null);

  const load = useCallback(
    async (opts: { quiet?: boolean } = {}) => {
      const token = ++reqRef.current;
      const next = await fetchCattleTicker(
        lockBarn
          ? { barnSlug: lockBarn.slug, cattleType: view }
          : showMine
            ? // My Barns replaces the scope outright rather than adding to it —
              // the toggle clears state and barn when it goes on, so there is
              // no way to be looking at "Kansas AND my barns" and wonder which
              // one the empty board is telling you about.
              { barnSlugs: myBarns, cattleType: view }
            : { state, barn, cattleType: view },
        todayCentral(),
      );
      if (token !== reqRef.current) return; // a newer request is in flight

      // ⚠️ COULD NOT REACH US: keep whatever is on screen and say so. Replacing
      // real prices with an empty board is the one outcome that is worse than
      // showing nothing at all, because it reads as a fact about the market.
      if (!next.reachable) {
        setOffline(true);
        return;
      }
      setOffline(false);
      setSeenAt(centralTime(new Date().toISOString()));

      // Diff against what was on screen to find genuinely new prints. Only a
      // QUIET refresh (the timer) can produce an arrival — a scope change
      // swaps the whole board, and flashing all of it would mean nothing.
      const ids = new Set<string>();
      for (const b of next.bands) for (const l of b.lots) ids.add(l.id);
      const previous = seenRef.current;
      seenRef.current = ids;
      if (previous && opts.quiet) {
        const added = new Set<string>();
        for (const id of ids) if (!previous.has(id)) added.add(id);
        setFresh(added);
      }

      setData(next);
    },
    // ⚠️ myBarns IS IN HERE. Starring a barn while the toggle is on must
    // redraw the board; leaving it out would show a man a board that quietly
    // disagrees with the list he just edited.
    [state, barn, view, lockBarn, showMine, myBarns],
  );

  // Refresh whenever the scope changes, then on a timer. The tab's visibility
  // gates the timer: a board left open in a background tab for a week would
  // otherwise poll ~20,000 times to render pixels nobody is looking at. It
  // refreshes immediately on return.
  useEffect(() => {
    void load();

    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer === null) timer = setInterval(() => void load({ quiet: true }), POLL_MS);
    };
    const stop = () => {
      if (timer !== null) { clearInterval(timer); timer = null; }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") { void load({ quiet: true }); start(); }
      else stop();
    };

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load]);

  // Last week's averages, for the app's move and chart. Keyed on the day the
  // board resolved to rather than on a timer: the comparable day only changes
  // when the board's day does, and re-reading a settled week every 30 seconds
  // would triple the board's traffic to redraw the same dashed line.
  const prevDay = compact ? (data.summary?.prevDate ?? null) : null;
  useEffect(() => {
    if (!compact || !prevDay) { setCompare(new Map()); return; }
    let live = true;
    void fetchBandCompare(
      // ⚠️ THE SAME SCOPE AS THE BOARD, WHATEVER THAT SCOPE IS. This is the
      // "vs last week" arrow. Scope the board to a man's own barns and leave
      // this national and the arrow compares HIS barns this week against EVERY
      // barn in the country last week — a number that means nothing, on the
      // one mark a man reads fastest.
      lockBarn
        ? { barnSlug: lockBarn.slug, cattleType: view }
        : showMine
          ? { barnSlugs: myBarns, cattleType: view }
          : { state, barn, cattleType: view },
      prevDay,
    ).then((m) => {
      if (live) setCompare(m);
    });
    return () => { live = false; };
  }, [compact, prevDay, state, barn, view, lockBarn, showMine, myBarns]);

  // The barn picker's options — and, when the rail is on, its rows.
  //
  // ⚠️ IT POLLS WHEN THE RAIL IS SHOWING, AND ONLY THEN. The original comment
  // here read "refetched only when the STATE changes … which barns sold is
  // settled by mid-morning", and that was right while this fed a <select> of
  // barn NAMES. It became wrong the moment the same rows carried live receipts:
  // the owner asked to "show the receipts live as the come in", and a list
  // fetched once at mount shows the head count a barn had when the page opened.
  //
  // The picker keeps the cheap behaviour. Nothing else pays for the rail.
  useEffect(() => {
    let live = true;
    const load = () => {
      void fetchTickerBarns(state).then((list) => { if (live) setBarns(list); });
    };
    load();
    if (!liveRail) return () => { live = false; };
    // Same 30s as the board, matching the edge cache on rpc_cattle_ticker_barns.
    const t = setInterval(load, POLL_MS);
    return () => { live = false; clearInterval(t); };
  }, [state, liveRail]);

  // Resolve open/closed after mount, then re-check every minute so a board left
  // open through the end of a sale flips to "Closed" by itself.
  useEffect(() => {
    const tick = () => setStatus(marketStatus(data.latestAt));
    tick();
    const t = setInterval(tick, 60_000);
    return () => clearInterval(t);
  }, [data.latestAt]);

  // Clear the flash after it has played.
  useEffect(() => {
    if (!fresh.size) return;
    const t = setTimeout(() => setFresh(new Set()), FLASH_MS);
    return () => clearTimeout(t);
  }, [fresh]);

  const states = useMemo(
    () => [...new Set(barns.map((b) => b.state).filter(Boolean))].sort(),
    [barns],
  );
  const visibleBarns = state ? barns.filter((b) => b.state === state) : barns;
  /* A badge on every row of the Steers tab is noise on every line; on a mixed
     tab it is the difference between reading one market and four. */
  const showClass = viewMixesClasses(view);

  // The filter row's two new controls (owner, 2026-10-08, from the design
  // mock): one weight band at a time, and a barn search that sets the barn.
  const [bandOnly, setBandOnly] = useState<number | null>(null);
  const [barnQ, setBarnQ] = useState("");

  // Live · Live Prices | Map. Lives in the head on a desktop and in the Filter
  // drawer on a phone, so it is built once and placed twice rather than
  // written twice. The lamp rides in the group as its first segment (owner,
  // 2026-10-08, from the mock's Live | Board | Map); it is a status, not a
  // button — it says Live or Closed and cannot be pressed.
  const viewToggle = (
    <div className="rbtx-viewtoggle" role="group" aria-label="View">
      <span className="rbtx-viewbtn rbtx-viewlive" data-state={status ? (status.open ? "open" : "closed") : "unknown"}>
        <i aria-hidden="true" />
        {status?.open ? "Live" : "Closed"}
      </span>
      {([["live", "Live Prices"], ["board", "Ticker"], ["map", "Map"]] as const)
        .filter(([key]) => key !== "live" || liveView || below)
        .map(([key, label]) => (
        <button
          key={key}
          type="button"
          className="rbtx-viewbtn"
          data-on={boardView === key ? "1" : undefined}
          aria-pressed={boardView === key}
          onClick={() => { pickedView.current = true; setBoardView(key); }}
        >
          {label}
        </button>
      ))}
    </div>
  );

  // Scope, State and Barn — the same three controls in both heads.
  //
  // ⚠️ THEY ARE REPLACED, NOT JUST IGNORED, WHEN MY BARNS IS ON. Owner,
  // 2026-09-20: "When I switch to my barns I don't think the tickers filtered".
  // It had filtered — two barns and 272 lots against the country's 21 and
  // 2,257 — but the head above still read "Scope: United States" and "Barn:
  // All 21 barns", because those controls carry their own state and neither
  // knew the board had moved. Twenty-one is the NATIONAL count. A man reads
  // the label, and the label said the opposite of the truth.
  //
  // Leaving them live and merely wrong was never an option; leaving them live
  // and correct is not one either, because picking a state here would mean two
  // scopes at once. So while My Barns is on, the three controls become one
  // line that says what is actually on screen.
  const pickers = showMine ? (
    <div className="rbtx-select rbtx-select-mine">
      <span className="rbtx-select-label">Scope</span>
      <strong className="rbtx-mine-scope">
        My Barns · {myBarns?.length === 1 ? "1 barn" : `${myBarns?.length} barns`}
      </strong>
    </div>
  ) : (
    <>
      {/* ⚠️ SCOPE IS GONE (owner, 2026-10-08, matching the design mock).
          It was the same control as State — both set the state — kept twice
          per an older artboard and hidden on phones as a duplicate. One
          control now, on every screen. Changing state orphans the chosen
          barn; clearing it is the only honest move — a Nebraska barn kept
          under "Kansas" would show Nebraska prices under a Kansas heading. */}
      <label className="rbtx-select rbtx-select-state">
        <span className="rbtx-select-label">State</span>
        <select
          value={state ?? ""}
          onChange={(e) => { setState(e.target.value || null); setBarn(null); }}
          aria-label="State"
        >
          <option value="">All states</option>
          {states.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </label>

      <label className="rbtx-select rbtx-select-barn">
        <span className="rbtx-select-label">Barn</span>
        {/* Only barns that actually SOLD on the day being shown. A picker
            full of barns that did not sell is a list of dead ends. */}
        <select
          value={barn ?? ""}
          onChange={(e) => setBarn(e.target.value || null)}
          aria-label="Sale barn"
        >
          <option value="">
            {visibleBarns.length ? `All ${visibleBarns.length} barns` : "No barns selling"}
          </option>
          {visibleBarns.map((b) => (
            <option key={b.name} value={b.name}>
              {b.name} · {b.state} · {b.headLabel}
            </option>
          ))}
        </select>
      </label>

      {/* One weight band, or all six (owner, 2026-10-08, from the mock). */}
      <label className="rbtx-select rbtx-select-weight">
        <span className="rbtx-select-label">Weight class</span>
        <select
          value={bandOnly ?? ""}
          onChange={(e) => setBandOnly(e.target.value ? Number(e.target.value) : null)}
          aria-label="Weight class"
        >
          <option value="">All</option>
          {data.bands.map((b) => (
            <option key={b.band} value={b.band}>{b.label} lb</option>
          ))}
        </select>
      </label>
    </>
  );

  // Type a barn, pick it, and the board is that barn's — the same thing the
  // Barn dropdown does, for a man who knows the name (owner, 2026-10-08, from
  // the mock's "Search barns…"). Only barns that sold on the day shown.
  const barnHits = barnQ.trim().length >= 2
    ? visibleBarns.filter((b) => b.name.toLowerCase().includes(barnQ.trim().toLowerCase())).slice(0, 8)
    : [];
  const barnSearch = showMine ? null : (
    <div className="rbtx-select rbtx-select-search" role="search">
      <svg className="rbtx-select-search-ic" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.8-3.8" />
      </svg>
      <input
        type="search"
        value={barnQ}
        placeholder="Search barns…"
        aria-label="Search barns"
        autoComplete="off"
        onChange={(e) => setBarnQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && barnHits[0]) { setBarn(barnHits[0].name); setBarnQ(""); }
          else if (e.key === "Escape") setBarnQ("");
        }}
      />
      {barnHits.length > 0 && (
        <div className="rbtx-barnsearch-panel">
          {barnHits.map((b) => (
            <button key={b.name} type="button" onClick={() => { setBarn(b.name); setBarnQ(""); }}>
              <b>{b.name}</b>
              <span>{b.state} · {b.headLabel}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // A fragment, NOT a .rbtx-left wrapper. The artboard's left column holds the
  // ticker AND the picks strip beneath it, so the column belongs to the page;
  // owning it here put the picks outside the column and stranded them under
  // the whole stage, below the rail.
  return (
    <>
      <TickerHead
        title={lockBarn ? lockBarn.name : lockState ? lockState.name : "Cattle"}
        accent={lockBarn || lockState ? undefined : "Ticker"}
        /* The app has no masthead above the board, so the marks ride in the
           head itself. The website's are in TickerChrome; passing them here too
           would show them twice on one screen. */
        brand={
          compact ? (
            <>
              <img className="rbtx-mark-rb" src="/brand/rb-white.png" alt="RaisedBeef" />
              <span className="rbtx-marks-rule" aria-hidden="true" />
              <span className="rbtx-poweredby">
                <span className="rbtx-poweredby-l">Powered by</span>
                <img className="rbtx-mark-dv" src="/brand/dv-mark.png" alt="DVAuction" />
              </span>
            </>
          ) : undefined
        }
        /* The scope, said once, in the badge — so the title does not grow a
           subtitle when someone picks a state. */
        badge={lockBarn ? lockBarn.state : barn ? "Barn" : (state ?? "U.S.")}
        status={status}
        date={{ day: data.day, label: data.day ? data.headline : null }}
        meta={
          offline && compact ? (
            /* ⚠️ SAY WHICH IT IS. "No sales" and "we cannot reach you" are
               different facts and the board used to show the first for both. */
            <b className="rbtx-offline">
              Can&rsquo;t reach RaisedBeef
              {seenAt ? ` · showing ${seenAt}` : " · no prices yet"}
            </b>
          ) : (
            <>
              {status ? (status.open ? "A few minutes behind the barn" : status.detail) : "Live auction prices"}
              {" · 5+ hd under 500 lb, 10+ over"}
            </>
          )
        }
        right={
          <>
            {viewToggle}
            {/* The day's barn count in the head (owner, 2026-10-08, from the
                mock's "57 barns sold today"). "Reported", because the day
                shown is often yesterday — the date beside it says which. No
                Download: the mock had one, the owner did not want it. */}
            {data.summary?.barns && (
              <span className="rbtx-headstat">
                <b>{data.summary.barns}</b>
                <span>Barns reported</span>
              </span>
            )}
          </>
        }
        /* ⚠️ THE CONTROLS STAY OUT IN THE OPEN, on the app as on the website
           (owner, 2026-09-16: "I want the buttons not the filter button").
           They were briefly folded behind a Filter button to buy back the first
           screen; the screen came back when the board itself was stripped, and a
           control a man cannot see is a control he does not know he has. */
        filters={
          <>
            {/* No State, Barn or Weight picker when the barn IS the page — a
                picker that can only navigate away from what you opened is a
                trap. */}
            {!lockBarn && !lockState && pickers}
            <ClassTabs view={view} onView={setView} />
            {!lockBarn && !lockState && barnSearch}
          </>
        }
      />

      {/* Status- and date-aware, which this band was NOT until 2026-09-13: it
          read "rings live now" every hour of every day, including on a Saturday
          two inches under the masthead lamp reading CLOSED. `status` is null on
          the server and on the first client paint and resolves in an effect, so
          the prerendered document and the browser's first render still agree —
          the same mechanism the lamp on line 175 has always used. */}
      <StatementBand
        /* On the website homepage the count is in the head (above); saying it
           twice on one screen is clutter. The barn and state boards keep it. */
        rings={liveRail ? null : (data.summary?.barns ?? null)}
        status={status}
        day={data.day}
      />

      {/* ── WHOSE PRICES THESE ARE (owner, 2026-09-14) ───────────────────
          "When I select Joplin it's hard to understand where the lots are
          coming from… can there be a banner that says viewing Joplin prices?"
          Said once, above the board, with the way back out. The U.S. view
          needs no banner — each lot names its barn in small print instead. */}
      {showMine ? (
        /* ⚠️ THE DATE MOVES, AND THAT NEEDS SAYING. The board shows the most
           recent day any of HIS barns sold, which is often not the country's
           last sale day — Sep 15 against Sep 19 on the owner's own three. A
           date that jumps with no explanation reads as a bug; said out loud it
           reads as the answer to "when did my barns last sell". */
        /* The SAME banner the barn and state scopes use, so this reads as one
           more scope rather than a new kind of thing. */
        <div className="rbtx-scopebanner" role="status">
          <span className="rbtx-scopebanner-k">Viewing</span>
          <span className="rbtx-scopebanner-v">
            <b className="rbtx-scopebanner-name">
              your {myBarns?.length === 1 ? "barn" : `${myBarns?.length} barns`}
            </b>
            <small>
              {/* ⚠️ summary.barns is a FORMATTED STRING, not a number — `num()`
                  has already been through it. "0" is truthy, so the count is
                  read back out rather than tested for truthiness, or a day
                  when none of his barns sold would read "0 sold". */}
              {Number(data.summary?.barns ?? 0) > 0
                ? ` ${data.summary?.barns} sold`
                : " none sold"}
              {/* ⚠️ THE DAY, SAID OUT LOUD. The board shows the last day any of
                  HIS barns sold, which is often not the country's — Sep 15
                  against Sep 19 on the owner's own three. A date that moves
                  with no explanation reads as a fault; named, it is the answer
                  to "when did my barns last sell". */}
              {data.dayLabel ? ` · ${data.dayLabel}` : ""}
            </small>
          </span>
          <span className="rbtx-scopebanner-actions">
            <button type="button" onClick={() => setMine(false)}>All barns ✕</button>
          </span>
        </div>
      ) : (
        // A state-locked board IS the state; a banner offering "All states ✕"
        // would undo the page it sits on.
        (barn || state) && !lockState && (
          <ScopeBanner
            barn={barn ? (barns.find((b) => b.name === barn) ?? { name: barn, state: state ?? "", head: 0, headLabel: "" }) : null}
            state={state}
            barnCount={visibleBarns.length}
            onClear={() => {
              if (barn) setBarn(null);
              else setState(null);
            }}
          />
        )
      )}

      {/* My Barns — app only, and only once he has starred something.
          Deliberately OUTSIDE the Filter button the compact head hides its
          pickers behind: this is the control the owner asked to be on the home
          screen, not one more thing to go looking for. */}
      {!lockBarn && !lockState && (myBarns?.length ?? 0) > 0 && (
        <div className="rbtx-mine" role="group" aria-label="Which barns to show">
          <button
            type="button"
            className="rbtx-mine-b"
            data-on={showMine ? "1" : undefined}
            aria-pressed={showMine}
            onClick={() => {
              // Turning it on clears the other scopes — see the note in load().
              setState(null);
              setBarn(null);
              setMine(true);
            }}
          >
            My Barns
            <span className="rbtx-mine-n">{myBarns?.length}</span>
          </button>
          <button
            type="button"
            className="rbtx-mine-b"
            data-on={!showMine ? "1" : undefined}
            aria-pressed={!showMine}
            onClick={() => setMine(false)}
          >
            All
          </button>
        </div>
      )}

      <div className="rbtx-boardwrap">
        {showMap ? (
          <Suspense fallback={<div className="rbtx-band-empty">Loading the map…</div>}>
            <TickerMap barns={barns} dayLabel={data.dayLabel} summary={data.summary} />
          </Suspense>
        ) : (
          // ⚠️ NO TAPE ON THE HOMEPAGE (owner, 2026-09-13): the crawling strip
          // came out. Every print it carried is on the board below in a form
          // that holds still. Barn tickers lost it on 2026-09-14; state tickers still run TapeCrawl.
          // The board and, on the website homepage, the live barn rail beside
          // it. data-rail drives the 4→3 column change in tickerHome.css so the
          // grid only narrows where the rail actually takes the space.
          <div className="rbtx-boardsplit" data-rail={liveRail ? "1" : undefined}>
            {/* ⚠️ THE RAIL IS FIRST IN THE DOM BECAUSE IT IS FIRST ON SCREEN
                (owner, 2026-09-30: "Can we move the new barns today to the
                left side and not the right"). Leaving it last and moving it
                with CSS `order` would put the keyboard's tab order and the
                screen reader's reading order on the opposite side of the page
                from the eye. On a phone it stacks BELOW the board instead —
                see the order rule in tickerHome.css — because prices come
                before a directory when there is only one column. */}
            {/* `day` is the board's own sale date — the menu prints it beside
                each barn's head count so the reader knows which day's run they
                are looking at (owner, 2026-10-01). */}
            {/* The cell is what the grid sizes; the rail fills it (tickerHome.css,
                "EVEN COLUMNS"). So the row is the board's height and the rail
                scrolls inside it — the three columns end on one line (owner,
                2026-10-25: "the barn column should end at the bottom of our
                weight columns in the middle and scroll beyond that"). */}
            {liveRail && <div className="rbtx-lbr-cell"><LiveBarnRail barns={barns} day={data.day} /></div>}
            {boardView === "live" ? (
              // The Daily Live Price board in the ticker's place (owner,
              // 2026-10-08: "when the toggle to live prices I want the daily
              // live price index board that is below the ticker to actually
              // replace the ticker").
              <div className="rbtx-boardcol rbtx-boardcol-live">{liveView ?? below}</div>
            ) : (() => {
              const board = (
                <BandBoard
                  bands={bandOnly === null ? data.bands : data.bands.filter((b) => b.band === bandOnly)}
                  showClass={showClass}
                  showBarn
                  /* Filled navy band headers, homepage only — see BandBoard's
                     `homeCard` doc for why this is opt-in rather than the
                     component's new default. */
                  homeCard
                  fresh={fresh}
                  rich={compact}
                  compare={compact ? compare : undefined}
                  compareLabel={compact ? vsWeekdayLabel(prevDay) : undefined}
                  /* The ninth card (owner, 2026-10-25): eight bands three across
                     left a hole beside 1000–1099. The day's totals fill it. */
                  tail={liveRail && data.summary ? <TodayCard summary={data.summary} dayLabel={data.dayLabel} /> : undefined}
                />
              );
              // One column for the bands and what sits under them, so the
              // split stays rail + board (see `below`).
              return below ? <div className="rbtx-boardcol">{board}{below}</div> : board;
            })()}
          </div>
        )}
      </div>
    </>
  );
}

/** The day in one card, in the band cards' own dress: head, lots, barns, the
 *  class mix, and how the run compares with a typical same weekday. Homepage
 *  only — it is BandBoard's `tail`, the cell after the last band. */
function TodayCard({ summary, dayLabel }: { summary: TickerSummary; dayLabel: string }) {
  const mix = summary.mix.filter((m) => m.head > 0);
  return (
    <section className="rbtx-today" aria-label="Today's run">
      <div className="rbtx-bandhead">
        <div className="rbtx-bandlabel">Today · <b>{dayLabel}</b></div>
      </div>
      <div className="rbtx-today-body">
        <div className="rbtx-today-big"><b>{summary.head}</b><span>head sold</span></div>
        {/* vsTypicalPhrase, never the old "of a typical" wording: the SIZE of the change is
            unsigned and the direction is in `dir` — the 2026-09-14 launch audit caught a 176%
            day told as a slow one by exactly that mistake. On its own line: beside the date in
            the head it ran off the card. */}
        {summary.vsTypical && (
          <p className="rbtx-today-vs" data-dir={summary.dir ?? undefined}>{vsTypicalPhrase(summary.vsTypical, summary.dir, dayLabel.split(",")[0]?.trim() || "day")}</p>
        )}
        <dl className="rbtx-today-facts">
          <div><dt>Barns</dt><dd>{summary.barns}</dd></div>
          <div><dt>Lots</dt><dd>{summary.lots}</dd></div>
          {mix.map((m) => <div key={m.label}><dt>{m.label}</dt><dd>{m.value}</dd></div>)}
        </dl>
      </div>
    </section>
  );
}

/** "Viewing Joplin Regional Stockyards, MO" / "Viewing Missouri barns". */
function ScopeBanner({
  barn,
  state,
  barnCount,
  onClear,
}: {
  barn: TickerBarn | null;
  state: string | null;
  barnCount: number;
  onClear: () => void;
}) {
  const stateName = state ? (STATE_NAME[state] ?? state) : null;
  return (
    <div className="rbtx-scopebanner" role="status">
      <span className="rbtx-scopebanner-k">Viewing</span>
      <span className="rbtx-scopebanner-v">
        {/* The name in its own element so a phone can shorten it with "…"
            while the state and the button stay whole (owner, 2026-09-14: "I
            want it slimmer"). */}
        {barn ? (
          <>
            <b className="rbtx-scopebanner-name">{barn.name}</b>
            {barn.state ? <span className="rbtx-scopebanner-state">, {barn.state}</span> : null}
            <small> only this barn's lots</small>
          </>
        ) : (
          <>
            <b className="rbtx-scopebanner-name">{stateName} barns</b>
            <small> {barnCount === 1 ? "1 barn selling" : `${barnCount} barns selling`}</small>
          </>
        )}
      </span>
      <span className="rbtx-scopebanner-actions">
        {/* Plain <a>: this document mounts no Router. */}
        {barn && barn.state && (
          <a href={`/barn/${barnSlug(barn.name, barn.state)}/ticker`}>This barn's ticker →</a>
        )}
        <button type="button" onClick={onClear}>
          {barn ? (state ? `All ${stateName ?? state} barns` : "All barns") : "All states"} ✕
        </button>
      </span>
    </div>
  );
}
